import { PLACE_SUBSCRIPTION_TYPE } from '@/constants';
import { __ } from '@/locale';
import { SubscriptionType } from '@/types/place';
import { Place, PlaceCampaign } from '@/types/state/shared';
import { secondsToHour } from './date';
import { isEmpty } from './objects';
import { server } from './serverRequests';
import { promiseWrapper } from './utils';

export const getImages = (place: Place) => {
  const { profileImage, images = [] } = place.about || {};
  const galleryImages = [...images];
  if (!galleryImages.length) {
    if (profileImage) {
      galleryImages.push(profileImage);
    } else {
      return null;
    }
  }

  return galleryImages;
};

export const getServices = ({ serviceIds, place }: { serviceIds: Array<number>; place: any }) => {
  const services = [];

  if (!Array.isArray(place.services)) {
    return null;
  }

  place.services.forEach((category) => {
    if (!Array.isArray(category.services)) {
      return;
    }

    category.services.forEach((service) => {
      if (!service.id) {
        return;
      }

      serviceIds.forEach((serviceId) => {
        if (serviceId === service.id) {
          services.push(service);
        }
      });
    });
  });

  return services.length > 0 ? services : null;
};

export const getEmployee = ({ employeeId, place }: { employeeId: number; place: any }) => {
  if (!Array.isArray(place.employees)) {
    return null;
  }
  return place.employees.filter((employee) => employee.id === employeeId)[0];
};

export const fetchPlace = async ({
  id,
  slug,
  isSistaminuten,
  dealId,
  abortSignal,
}: {
  id: number;
  slug: string;
  isSistaminuten?: boolean;
  dealId?: number;
  abortSignal: AbortSignal;
}) => {
  if (!id || !slug) {
    return null;
  }

  let url = `/findPlace/${id}/${encodeURIComponent(slug)}?slug=${encodeURIComponent(slug)}`;

  if (isSistaminuten) {
    url += '&sistaminuten=1';
  } else if (dealId) {
    url += '&dealId=' + dealId;
  }

  const { data } = await promiseWrapper(server.request.get(url, { signal: abortSignal }));

  const fetchedPlace = data.data.place;

  return fetchedPlace;
};

export const formatPhoneNumber = (phoneNumber: string) => {
  if (phoneNumber.length === 10) {
    return {
      fullNumber: `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 8)} ${phoneNumber.slice(
        8,
        10,
      )}`,
      hiddenNumber: `${phoneNumber.slice(0, 3)}-${phoneNumber.slice(3, 6)} ${phoneNumber.slice(6, 8)} XX`,
    };
  } else if (phoneNumber.length > 2) {
    return {
      fullNumber: phoneNumber,
      hiddenNumber: phoneNumber.slice(0, -2) + 'XX',
    };
  }
  return {
    fullNumber: phoneNumber,
    hiddenNumber: phoneNumber,
  };
};

export const getBreadcrumbItems = (place: any): { title: string; url: string }[] => {
  const mainCategory = place.about?.category?.main;
  const subCategory = place.about?.category?.sub;

  const items = [
    { title: __('Home'), url: '/' },
    ...(mainCategory ? [{ title: mainCategory, url: `/${mainCategory}/var` }] : []),
    ...(subCategory ? [{ title: subCategory, url: `/${subCategory}/var` }] : []),
  ];

  return items;
};

type OpeningHours = Record<string, { from?: number; to?: number }[]>;

export const getOpenHoursStrings = (openingHours: OpeningHours) => {
  const WEEKDAYS = {
    '1': 'monday',
    '2': 'tuesday',
    '3': 'wednesday',
    '4': 'thursday',
    '5': 'friday',
    '6': 'saturday',
    '0': 'sunday',
  };

  const openHours = Object.entries(WEEKDAYS).reduce((acc, [key, value]) => {
    const hours = openingHours?.[key];

    if (!hours || hours.length === 0) {
      return acc;
    }

    acc[value] = `${secondsToHour(hours[0].from)} - ${secondsToHour(hours[0].to)}`;
    return acc;
  }, {});

  if (isEmpty(openHours)) {
    return undefined;
  }
  return openHours;
};

export const getMerchantKlarnaClientId = (place: Place) => {
  const { employees } = place;
  return employees?.find((employee) => !!employee.about.settings?.klarnaClientId)?.about?.settings?.klarnaClientId;
};

export const getGiftcardCheckoutLink = (place: Place) => {
  if (!place.about?.settings?.sellsGiftCard && !place.about?.settings?.sellsValueCard) {
    return null;
  }
  let url = `/places/${place.about.slug}-${place.id}`;

  if (place.about.settings.sellsValueCard) {
    url += '/valuecards/buy';
  } else {
    url += '/giftcard/checkout';
  }
  return url;
};

export const getUrlWithProtocol = (oldUrl: string) => {
  let url = (oldUrl || '').trim();
  const hasProtocol = /^(https?:\/\/)/i.test(url);
  const hasWhitespace = /\s/.test(url);
  if (url && !hasWhitespace && !hasProtocol) {
    url = 'http://' + url;
  }
  return url;
};

export function isProfilePageSubscription(type: SubscriptionType): boolean {
  return (
    type === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_PREMIUM || type === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_VERIFIED
  );
}

export function isBokadirektPayCheckout(
  employee: { about?: { settings?: { usePlatformCheckout?: boolean | null } } } | undefined,
): boolean {
  return !!employee?.about?.settings?.usePlatformCheckout;
}

export type BokadirektPayBadges = { klarna: boolean; walley: boolean };

type BokadirektPayBadgeEmployee =
  | {
      about?: {
        settings?: {
          usePlatformCheckout?: boolean | null;
          onlinePaymentForSistaminutenOnly?: boolean | null;
        };
      };
    }
  | undefined;

/**
 * Whether a Bokadirekt Pay employee's Klarna and Walley badges may show at all. Employees without
 * Bokadirekt Pay never get them; their badges come from the legacy flags.
 */
export function showsBokadirektPayBadges(employee: BokadirektPayBadgeEmployee, isSistaminuten: boolean): boolean {
  return (
    isBokadirektPayCheckout(employee) &&
    (isSistaminuten || !employee?.about?.settings?.onlinePaymentForSistaminutenOnly)
  );
}

/**
 * The Klarna and Walley badges a Bokadirekt Pay employee gets, given the methods their
 * merchant has configured.
 */
export function getBokadirektPayBadges(
  employee: BokadirektPayBadgeEmployee,
  methods: Partial<BokadirektPayBadges> | undefined,
  isSistaminuten: boolean,
): BokadirektPayBadges {
  const shown = showsBokadirektPayBadges(employee, isSistaminuten);
  return {
    klarna: shown && !!methods?.klarna,
    walley: shown && !!methods?.walley,
  };
}

export function hasDealCampagin(campaigns: PlaceCampaign[]) {
  if (!campaigns) return undefined;
  return (campaigns || []).find((campaign) => campaign?.publicationType === 2 && campaign.type === 7);
}
